import { useQuery } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@veil/ui/components/chart";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@veil/ui/components/empty";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatRelative } from "@veil/ui/lib/format";
import CheckCircle2 from "lucide-react/dist/esm/icons/check-circle-2";
import RefreshCcw from "lucide-react/dist/esm/icons/refresh-ccw";
import Server from "lucide-react/dist/esm/icons/server";
import XCircle from "lucide-react/dist/esm/icons/x-circle";
import { useMemo } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from "recharts";
import { Link } from "wouter";
import { QueryError } from "@/components/query-state";
import { SectionCards } from "@/components/section-cards";
import { StatusBadge } from "@/components/status-badge";
import { UpstreamDriftCard } from "@/components/upstream-drift-card";
import { api, unwrap } from "@/lib/api";
import { FLEET_STATUS_REFETCH_MS } from "@/lib/fleet-intervals";

const FLEET_SKELETON_KEYS = [
	"fleet-skel-1",
	"fleet-skel-2",
	"fleet-skel-3",
	"fleet-skel-4",
	"fleet-skel-5",
] as const;

interface WorkerSummary {
	hostname: string;
	hasData: boolean;
	enabled: boolean;
	weight: number;
	snapshot: null | {
		collectedAt: string;
		reachable: boolean;
		apiVersion: string | null;
		latestBuilds: Record<string, string> | null;
		latestProfiles: Record<string, string> | null;
		cronLastRun: string | null;
		diskFreeGB: number | null;
	};
}

interface WorkersResponse {
	success: boolean;
	hosts: WorkerSummary[];
}

interface OperationRow {
	id: string;
	kind: string;
	status: string;
	startedAt: string | Date;
	finishedAt: string | Date | null;
	user: { username: string } | null;
}

function toIsoString(value: string | Date | null | undefined): string | null {
	if (!value) return null;
	if (value instanceof Date) return value.toISOString();
	return value;
}

interface OperationsResponse {
	success: boolean;
	data: OperationRow[];
}

export function Dashboard() {
	const fleet = useQuery({
		queryKey: ["workers"],
		queryFn: () => unwrap(api.api.workers.get()) as Promise<WorkersResponse>,
		refetchInterval: FLEET_STATUS_REFETCH_MS,
	});
	const ops = useQuery({
		queryKey: ["operations", "recent"],
		queryFn: () =>
			unwrap(
				api.api.operations.get({ query: { pageSize: 50 } }),
			) as Promise<OperationsResponse>,
		refetchInterval: 10_000,
	});

	return (
		<div className="@container/main flex flex-col gap-6">
			<div className="flex flex-col gap-1">
				<h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
				<p className="text-sm text-muted-foreground">
					Cluster health, release activity, and worker status at a glance.
				</p>
			</div>

			<SectionCards />

			<UpstreamDriftCard variant="compact" />

			{/* One error surface for both operations panels: they share a
			    query, so two identical retry cards would be noise. */}
			{ops.isError ? (
				<QueryError
					title="Couldn't load operations"
					error={ops.error}
					onRetry={() => void ops.refetch()}
					retrying={ops.isFetching}
				/>
			) : (
				<>
					<OperationsChart
						operations={ops.data?.data ?? []}
						loading={ops.isLoading}
					/>

					<OperationsTable
						operations={ops.data?.data ?? []}
						loading={ops.isLoading}
					/>
				</>
			)}

			{fleet.isError ? (
				<QueryError
					title="Couldn't load workers"
					error={fleet.error}
					onRetry={() => void fleet.refetch()}
					retrying={fleet.isFetching}
				/>
			) : (
				<FleetStrip
					hosts={fleet.data?.hosts ?? []}
					loading={fleet.isLoading}
					onRefresh={() => void fleet.refetch()}
					refreshing={fleet.isFetching}
				/>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Operations volume — last 7 days, stacked by status
// ---------------------------------------------------------------------------

const chartConfig = {
	succeeded: { label: "Succeeded", color: "var(--chart-2)" },
	failed: { label: "Failed", color: "var(--destructive)" },
	running: { label: "Running", color: "var(--chart-4)" },
} satisfies ChartConfig;

function OperationsChart({
	operations,
	loading,
}: {
	operations: OperationRow[];
	loading: boolean;
}) {
	const data = useMemo(() => {
		const days = 7;
		const buckets: Array<{
			date: string;
			succeeded: number;
			failed: number;
			running: number;
		}> = [];
		const now = new Date();
		now.setHours(0, 0, 0, 0);
		for (let i = days - 1; i >= 0; i--) {
			const day = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
			buckets.push({
				date: day.toISOString().slice(0, 10),
				succeeded: 0,
				failed: 0,
				running: 0,
			});
		}
		const byDay = new Map(buckets.map((b) => [b.date, b]));
		for (const op of operations) {
			const iso = toIsoString(op.startedAt);
			if (!iso) continue;
			const day = iso.slice(0, 10);
			const bucket = byDay.get(day);
			if (!bucket) continue;
			if (op.status === "succeeded") bucket.succeeded += 1;
			else if (op.status === "failed") bucket.failed += 1;
			else if (op.status === "running") bucket.running += 1;
		}
		return buckets;
	}, [operations]);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Operations volume</CardTitle>
				<CardDescription>Per status, last 7 days</CardDescription>
			</CardHeader>
			<CardContent>
				{loading ? (
					<Skeleton className="h-48 w-full" />
				) : (
					<ChartContainer config={chartConfig} className="h-48 w-full">
						<ResponsiveContainer width="100%" height="100%">
							<AreaChart data={data}>
								<defs>
									<linearGradient
										id="fillSucceeded"
										x1="0"
										y1="0"
										x2="0"
										y2="1"
									>
										<stop
											offset="0%"
											stopColor="var(--color-succeeded)"
											stopOpacity={0.6}
										/>
										<stop
											offset="100%"
											stopColor="var(--color-succeeded)"
											stopOpacity={0.05}
										/>
									</linearGradient>
									<linearGradient id="fillFailed" x1="0" y1="0" x2="0" y2="1">
										<stop
											offset="0%"
											stopColor="var(--color-failed)"
											stopOpacity={0.6}
										/>
										<stop
											offset="100%"
											stopColor="var(--color-failed)"
											stopOpacity={0.05}
										/>
									</linearGradient>
									<linearGradient id="fillRunning" x1="0" y1="0" x2="0" y2="1">
										<stop
											offset="0%"
											stopColor="var(--color-running)"
											stopOpacity={0.6}
										/>
										<stop
											offset="100%"
											stopColor="var(--color-running)"
											stopOpacity={0.05}
										/>
									</linearGradient>
								</defs>
								<CartesianGrid vertical={false} strokeDasharray="3 3" />
								<XAxis
									dataKey="date"
									tickLine={false}
									axisLine={false}
									tickFormatter={(value: string) =>
										new Date(value).toLocaleDateString(undefined, {
											month: "short",
											day: "numeric",
										})
									}
								/>
								<YAxis
									tickLine={false}
									axisLine={false}
									allowDecimals={false}
								/>
								<ChartTooltip content={<ChartTooltipContent />} />
								<Area
									type="monotone"
									dataKey="succeeded"
									stackId="1"
									stroke="var(--color-succeeded)"
									fill="url(#fillSucceeded)"
								/>
								<Area
									type="monotone"
									dataKey="failed"
									stackId="1"
									stroke="var(--color-failed)"
									fill="url(#fillFailed)"
								/>
								<Area
									type="monotone"
									dataKey="running"
									stackId="1"
									stroke="var(--color-running)"
									fill="url(#fillRunning)"
								/>
							</AreaChart>
						</ResponsiveContainer>
					</ChartContainer>
				)}
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Operations table — last 10
// ---------------------------------------------------------------------------

function OperationsTable({
	operations,
	loading,
}: {
	operations: OperationRow[];
	loading: boolean;
}) {
	const rows = operations.slice(0, 10);
	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Recent operations</CardTitle>
				<CardDescription>
					<Link href="/operations" className="text-primary hover:underline">
						View all
					</Link>
				</CardDescription>
			</CardHeader>
			<CardContent className="p-0">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Kind</TableHead>
							<TableHead>Status</TableHead>
							<TableHead>Started</TableHead>
							<TableHead>By</TableHead>
							<TableHead className="text-right">Detail</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{loading && (
							<TableRow>
								<TableCell colSpan={5}>
									<Skeleton className="h-16 w-full" />
								</TableCell>
							</TableRow>
						)}
						{!loading &&
							rows.map((op) => (
								<TableRow key={op.id}>
									<TableCell className="font-mono text-xs">{op.kind}</TableCell>
									<TableCell>
										<StatusBadge status={op.status} />
									</TableCell>
									<TableCell>{formatRelative(op.startedAt)}</TableCell>
									<TableCell>{op.user?.username ?? "—"}</TableCell>
									<TableCell className="text-right">
										<Link
											href={`/operations/${op.id}`}
											className="text-primary hover:underline"
										>
											open
										</Link>
									</TableCell>
								</TableRow>
							))}
						{!loading && rows.length === 0 && (
							<TableRow>
								<TableCell colSpan={5}>
									<Empty>
										<EmptyHeader>
											<EmptyMedia variant="icon">
												<Server />
											</EmptyMedia>
											<EmptyTitle>No operations yet</EmptyTitle>
											<EmptyDescription>
												Run a release or trigger a worker poll to see activity
												here.
											</EmptyDescription>
										</EmptyHeader>
									</Empty>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Fleet strip — host tiles with empty state
// ---------------------------------------------------------------------------

function FleetStrip({
	hosts,
	loading,
	onRefresh,
	refreshing,
}: {
	hosts: WorkerSummary[];
	loading: boolean;
	onRefresh: () => void;
	refreshing: boolean;
}) {
	return (
		<section className="flex flex-col gap-3">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-base font-semibold">Workers</h2>
					<p className="text-xs text-muted-foreground">
						Latest snapshot per host
					</p>
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={onRefresh}
					disabled={refreshing}
				>
					<RefreshCcw />
					Refresh
				</Button>
			</div>
			{loading ? (
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
					{FLEET_SKELETON_KEYS.map((key) => (
						<Skeleton key={key} className="h-32 w-full" />
					))}
				</div>
			) : hosts.length === 0 ? (
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant="icon">
							<Server />
						</EmptyMedia>
						<EmptyTitle>No workers configured</EmptyTitle>
						<EmptyDescription>
							Add workers on the <Link href="/workers">Workers</Link> page to
							start collecting snapshots.
						</EmptyDescription>
					</EmptyHeader>
					<EmptyContent>
						<Button asChild variant="outline" size="sm">
							<Link href="/workers">Open workers</Link>
						</Button>
					</EmptyContent>
				</Empty>
			) : (
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
					{hosts.map((host) => (
						<FleetTile key={host.hostname} host={host} />
					))}
				</div>
			)}
		</section>
	);
}

function FleetTile({ host }: { host: WorkerSummary }) {
	const reachable = host.snapshot?.reachable ?? false;
	const builds = host.snapshot?.latestBuilds ?? {};
	const profiles = host.snapshot?.latestProfiles ?? {};
	return (
		<Card size="sm" className="transition-colors hover:bg-muted/50">
			<CardHeader>
				<CardTitle className="flex items-center gap-2 font-mono text-sm">
					<Server className="size-4" />
					<Link href={`/workers/${host.hostname}`} className="hover:underline">
						{host.hostname}
					</Link>
				</CardTitle>
				<CardDescription className="flex items-center gap-2">
					{reachable ? (
						<Badge variant="success">
							<CheckCircle2 className="size-3" /> reachable
						</Badge>
					) : (
						<Badge variant="destructive">
							<XCircle className="size-3" /> unreachable
						</Badge>
					)}
					{!host.enabled && <Badge variant="outline">disabled</Badge>}
				</CardDescription>
			</CardHeader>
			<CardContent className="flex flex-col gap-1.5 text-xs">
				{!host.hasData ? (
					<span className="text-muted-foreground">No data yet</span>
				) : (
					<>
						<div className="flex items-center gap-2">
							<span className="text-muted-foreground">api</span>
							<Badge variant="outline" className="font-mono">
								{host.snapshot?.apiVersion ?? "—"}
							</Badge>
						</div>
						{Object.entries(builds).map(([major, ver]) => (
							<div key={major} className="flex flex-wrap items-center gap-1">
								<Badge variant="secondary" className="font-mono">
									v{major}
								</Badge>
								<Badge variant="outline" className="font-mono">
									{ver}
								</Badge>
								{profiles[major] && (
									<Badge variant="outline" className="font-mono">
										p{profiles[major]}
									</Badge>
								)}
							</div>
						))}
						<div className="text-muted-foreground">
							cron {formatRelative(host.snapshot?.cronLastRun ?? null)}
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
}
